import React from 'react';
import { X, Plus, ChevronUp, ChevronDown, Check, Pencil, Search, RefreshCw, Trash2, Loader2, Scan } from 'lucide-react';

const CategoryManagementModal = ({
  isOpen,
  onClose,
  categoryModalType,
  categories,
  newCategoryName,
  setNewCategoryName,
  newCategoryKeywords,
  setNewCategoryKeywords,
  addCategory,
  editingCategory,
  setEditingCategory,
  editCategoryValue,
  setEditCategoryValue,
  updateCategory,
  deleteCategory,
  reorderCategories,
  formatCategoryName,
  // For messages category modal
  editingKeywordsCategory,
  setEditingKeywordsCategory,
  keywordsInputValue,
  setKeywordsInputValue,
  saveKeywordsForCategory,
  customCategoryKeywords,
  generateKeywordsFromCategory,
  scanForCategory,
  scanningCategory,
  // Email category specific props
  emailCategoryKeywords,
  setEmailCategoryKeywords,
  saveEmailCategoryKeywords,
  scanAndCategorizeEmails,
  scanningEmails
}) => {
  if (!isOpen) return null;

  const showKeywordsFeature = categoryModalType === 'messages' || categoryModalType === 'emailCategories';
  const keywordsData = categoryModalType === 'emailCategories' ? emailCategoryKeywords : customCategoryKeywords;

  const getDefaultCategories = () => {
    switch (categoryModalType) {
      case 'messages':
        return ['urgent', 'follow-up', 'client'];
      case 'calls':
        return ['family', 'friends', 'business'];
      case 'emailCategories':
        return []; // No default categories - users can add/delete all
      case 'emails':
      default:
        return ['inbox', 'sent', 'drafts', 'trash'];
    }
  };

  const getModalTitle = () => {
    switch (categoryModalType) {
      case 'messages':
        return 'Edit Message Categories';
      case 'calls':
        return 'Edit Call Categories';
      case 'emailCategories':
        return 'Edit Email Categories';
      case 'emails':
      default:
        return 'Edit Email Folders';
    }
  };

  const getPlaceholder = () => {
    return categoryModalType === 'emails' ? 'New folder name...' : 'New category name...';
  };

  const defaultCategories = getDefaultCategories();

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">
            {getModalTitle()}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>
        
        {/* Add New Category */}
        <div className="p-4 border-b border-gray-700">
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !categoryModalType === 'emailCategories' && addCategory()}
                placeholder={getPlaceholder()}
                className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                data-testid="new-category-input"
              />
              <button
                onClick={addCategory}
                disabled={!newCategoryName.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-1.5"
                data-testid="add-category-btn"
              >
                <Plus size={14} />
                Add
              </button>
            </div>
            
            {/* Keywords input for email categories */}
            {categoryModalType === 'emailCategories' && newCategoryName.trim() && (
              <div className="space-y-1">
                <label className="text-xs text-gray-400">Keywords to scan for (comma-separated):</label>
                <input
                  type="text"
                  value={newCategoryKeywords || ''}
                  onChange={(e) => setNewCategoryKeywords?.(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                  placeholder="e.g., invoice, payment, receipt, order..."
                  className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  data-testid="new-category-keywords-input"
                />
                <p className="text-xs text-gray-500">
                  Keywords will be auto-generated from the category name if left empty
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Category List */}
        <div className="p-4 max-h-80 overflow-y-auto space-y-2">
          {categories.map((cat, index, arr) => {
            const isDefault = defaultCategories.includes(cat);
            
            return (
              <div 
                key={cat}
                className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group"
              >
                {/* Reorder Buttons */}
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => index > 0 && reorderCategories(index, index - 1)}
                    disabled={index === 0}
                    className={`p-0.5 rounded transition-colors ${
                      index === 0 
                        ? 'text-gray-600 cursor-not-allowed' 
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                    title="Move up"
                    data-testid={`move-up-${cat}`}
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    onClick={() => index < arr.length - 1 && reorderCategories(index, index + 1)}
                    disabled={index === arr.length - 1}
                    className={`p-0.5 rounded transition-colors ${
                      index === arr.length - 1 
                        ? 'text-gray-600 cursor-not-allowed' 
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                    title="Move down"
                    data-testid={`move-down-${cat}`}
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>
                
                {editingCategory === cat ? (
                  <>
                    <input
                      type="text"
                      value={editCategoryValue}
                      onChange={(e) => setEditCategoryValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') updateCategory(cat, editCategoryValue);
                        if (e.key === 'Escape') {
                          setEditingCategory(null);
                          setEditCategoryValue('');
                        }
                      }}
                      className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm focus:border-purple-500 focus:outline-none"
                      autoFocus
                      data-testid={`edit-category-input-${cat}`}
                    />
                    <button
                      onClick={() => updateCategory(cat, editCategoryValue)}
                      className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"
                      title="Save"
                    >
                      <Check size={14} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingCategory(null);
                        setEditCategoryValue('');
                      }}
                      className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                      title="Cancel"
                    >
                      <X size={14} />
                    </button>
                  </>
                ) : editingKeywordsCategory === cat ? (
                  <div className="flex-1 space-y-2">
                    <div className="text-xs text-gray-400 mb-1">
                      Enter keywords (comma-separated):
                    </div>
                    <input
                      type="text"
                      value={keywordsInputValue}
                      onChange={(e) => setKeywordsInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveKeywordsForCategory(cat, keywordsInputValue);
                        if (e.key === 'Escape') {
                          setEditingKeywordsCategory(null);
                          setKeywordsInputValue('');
                        }
                      }}
                      placeholder="code, verification, otp, 2fa, pin..."
                      className="w-full px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm focus:border-purple-500 focus:outline-none"
                      autoFocus
                      data-testid={`keywords-input-${cat}`}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => saveKeywordsForCategory(cat, keywordsInputValue)}
                        className="flex-1 px-2 py-1 bg-green-600 hover:bg-green-500 text-white text-xs rounded flex items-center justify-center gap-1"
                      >
                        <Check size={12} /> Save Keywords
                      </button>
                      <button
                        onClick={() => {
                          setEditingKeywordsCategory(null);
                          setKeywordsInputValue('');
                        }}
                        className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm">
                        {formatCategoryName(cat)}
                        {isDefault && (
                          <span className="ml-2 text-xs text-gray-500">(default)</span>
                        )}
                      </span>
                      <button
                        onClick={() => {
                          setEditingCategory(cat);
                          setEditCategoryValue(formatCategoryName(cat));
                        }}
                        className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Rename"
                        data-testid={`edit-btn-${cat}`}
                      >
                        <Pencil size={14} />
                      </button>
                      {showKeywordsFeature && (
                        <>
                          <button
                            onClick={() => {
                              const existingKeywords = keywordsData?.[cat] || generateKeywordsFromCategory?.(cat) || [];
                              setKeywordsInputValue(existingKeywords.join(', '));
                              setEditingKeywordsCategory(cat);
                            }}
                            className="p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Edit keywords"
                            data-testid={`keywords-btn-${cat}`}
                          >
                            <Search size={14} />
                          </button>
                          <button
                            onClick={() => scanForCategory?.(cat)}
                            disabled={scanningCategory === cat}
                            className="p-1.5 text-gray-400 hover:text-purple-400 hover:bg-purple-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                            title={categoryModalType === 'emailCategories' ? 'Scan emails for this category' : 'Scan messages for this category'}
                            data-testid={`scan-btn-${cat}`}
                          >
                            {scanningCategory === cat ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <RefreshCw size={14} />
                            )}
                          </button>
                        </>
                      )}
                      {!isDefault && (
                        <button
                          onClick={() => deleteCategory(cat)}
                          className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete"
                          data-testid={`delete-btn-${cat}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    {/* Show current keywords */}
                    {showKeywordsFeature && keywordsData?.[cat] && (
                      <div className="mt-1 text-xs text-gray-500 truncate max-w-[250px]">
                        Keywords: {keywordsData[cat].slice(0, 5).join(', ')}{keywordsData[cat].length > 5 ? '...' : ''}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Modal Footer */}
        <div className="p-4 border-t border-gray-700 space-y-3">
          {/* Scan All Emails button for email categories */}
          {categoryModalType === 'emailCategories' && scanAndCategorizeEmails && (
            <button
              onClick={scanAndCategorizeEmails}
              disabled={scanningEmails || Object.keys(keywordsData || {}).length === 0}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-gray-700 disabled:to-gray-700 disabled:text-gray-500 text-white rounded-lg transition-all text-sm font-medium flex items-center justify-center gap-2"
              data-testid="scan-all-emails-btn"
            >
              {scanningEmails ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Scanning Emails...
                </>
              ) : (
                <>
                  <Scan size={16} />
                  Scan All Emails
                </>
              )}
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
            data-testid="close-category-modal-btn"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryManagementModal;
